import { useState, useEffect } from 'react';
import axios from 'axios';
import { Label } from '@/components/ui/label';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Check, ChevronsUpDown } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const CategorySelector = ({
  value,
  onChange,
  label = 'Category',
  required = false
}) => {
  const [open, setOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);

  const [selectedCategory, setSelectedCategory] = useState(null);

  useEffect(() => {
    fetchCategories();
  }, [searchQuery]);

  // ✅ If parent already has value (id), set selected category
  useEffect(() => {
    if (!value || categories.length === 0) return;

    const found = categories.find((c) => c.id === value);
    if (found) setSelectedCategory(found);
  }, [value, categories]);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/categories/search`, {
        params: { q: searchQuery }
      });

      setCategories(response.data);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <Label>
        {label} {required && '*'}
      </Label>

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            className="w-full justify-between mt-2"
          >
            {selectedCategory ? selectedCategory.name : 'Select category...'}
            <ChevronsUpDown className="ml-2 h-4 w-4 opacity-50" />
          </Button>
        </PopoverTrigger>

        <PopoverContent className="w-full p-0">
          <Command>
            <CommandInput
              placeholder="Search category..."
              value={searchQuery}
              onValueChange={setSearchQuery}
            />

            <CommandEmpty>
              {loading ? 'Loading...' : 'No category found.'}
            </CommandEmpty>

            <CommandGroup className="max-h-64 overflow-auto">
              {categories.map((category) => (
                <CommandItem
                  key={category.id}
                  value={category.name.toLowerCase()} // ✅ IMPORTANT
                  onSelect={() => {
                    setSelectedCategory(category);
                    onChange(category.id); // ✅ store only ID outside
                    setOpen(false);
                  }}
                >
                  <Check
                    className={`mr-2 h-4 w-4 ${
                      selectedCategory?.id === category.id
                        ? 'opacity-100'
                        : 'opacity-0'
                    }`}
                  />
                  <div className="font-medium">{category.name}</div>
                </CommandItem>
              ))}
            </CommandGroup>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
};
